import { CircularProgress } from '@mui/material';
// import ObjectSelectBox from 'components/boss/ObjectSelectBox';
import DoughnutChart from 'components/ui/charts/DoughnutChart';
import ObjectBlockMultiFilter from 'components/ui/filters/items/ObjectBlockMultiFilter';
import useAuth from 'hooks/useAuth';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useCurrency from 'hooks/useCurrency';
import useTopPanel from 'hooks/useTopPanel';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useQueries } from 'react-query';
import { useLocation } from 'react-router-dom';
import { doughnutTooltip } from 'utils/chartjsTooltips';
import DebtCard from './items/DebtCard';
import RoomQuantityCard from './items/RoomQuantityCard';
import ContractsModal from './ContractsModal';
import DebtsModal from './DebtsModal';

const Dashboard = () => {
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();
  const [{ user }] = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { currencyData } = useCurrency();
  const [hasError, setHasError] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDebts, setOpenDebts] = useState(false);
  const [dailyStatusSum, setDailyStatusSum] = useState(0);
  const { search } = useLocation();

  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('boss.dashboard.title')}</div>
    );
  }, [i18n.language]);

  const [analyticData, dailyPayments] = useQueries([
    {
      queryKey: ['analyticDashboard', search],
      queryFn: async function () {
        const response = await axiosPrivate.get(
          `/analytic/dashboard/index${search || ''}`
        );
        return response.data.data;
      },
      enabled: !hasError,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
    {
      queryKey: 'dailyPayments',
      queryFn: async function () {
        const response = await axiosPrivate.get('/dictionary/daily');
        return response.data.data;
      },
      onSuccess: (data) => {
        if (data && data.length > 0) {
          setDailyStatusSum(
            data.reduce((acc, curr) => acc + parseFloat(curr.sum), 0)
          );
        }
      },
      enabled: !hasError,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
  ]);

  const onClickMonthly = () => {
    if (!analyticData?.isFetching) setOpenDebts(true);
  };

  return (
    <div className='component-wrapper boss-dashboard-wrapper'>
      <div className='dashboard-actions'>
        <div className='dashboard-owner-greeting'>
          {t('boss.dashboard.welcome')},{' '}
          <span className='text-xl font-medium'>{user?.user?.name}</span>
        </div>
        <div className='max-w-[300px] w-full'>
          <ObjectBlockMultiFilter />
        </div>
      </div>

      <div className='w-full grid grid-cols-12 gap-4'>
        <div className='dashboard-debts-wrapper !items-start col-span-8'>
          <div className='w-full'>
            <div className='payments-title p-2 mb-4 h-1'></div>
            <DebtCard
              type='monthly'
              onClick={onClickMonthly}
              title={t('contract.view.header.monthlyLeft')}
              analyticData={analyticData}
            />
            <div className='payments-title p-2 h-1'></div>
            <DebtCard
              type='total'
              title={t('boss.dashboard.allDebts')}
              analyticData={analyticData}
            />
            <div className='dashboard-homes mt-4'>
              <div className='payments-title mb-2'>
                {t('boss.dashboard.homeStatus')}
              </div>
              <div className='grid grid-cols-12 gap-4'>
                {analyticData?.data?.homestatuses?.map((home) => (
                  <div
                    key={home?.id}
                    className='col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-4'
                  >
                    <RoomQuantityCard
                      status='free-homes'
                      value={home?.count}
                      analyticData={analyticData}
                      title={home?.name}
                      color={getHomeStatusColor(home?.id)}
                      icon={getHomeStatusIcon(home?.id)}
                      // title={t('common.filter.emptyHomesCount')}
                    />
                  </div>
                ))}
                <div className='col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-4'>
                  <RoomQuantityCard
                    status='sold-homes'
                    value={analyticData?.data?.contracts?.length}
                    analyticData={analyticData}
                    icon='bi bi-house-check'
                    title={t('boss.dashboard.soldToday')}
                    onClick={() => setOpen(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='w-full col-span-4'>
          <div className='payments-title mb-2 h-6'>
            {t('boss.payment.title')}
          </div>
          {dailyPayments.isLoading || dailyPayments.isFetching ? (
            <div className='flex w-full items-center justify-center'>
              <CircularProgress size={30} color='inherit' />
            </div>
          ) : (
            <div className='grid grid-cols-2 gap-2'>
              {dailyPayments?.data?.length ? (
                <>
                  {dailyPayments?.data?.map((payment, i) => (
                    <div
                      key={i}
                      className='rounded-lg !base-border p-4 flex gap-1 items-center justify-center flex-col shadow-md'
                    >
                      <i className={`${getIcon(payment?.name)} text-lg`} />
                      <div>{payment?.name}</div>
                      <div>
                        <NumericFormat
                          value={payment?.sum}
                          displayType={'text'}
                          allowNegative={false}
                          thousandSeparator={' '}
                          decimalScale={2}
                          className='bg-transparent font-bold'
                          suffix={' UZS'}
                        />
                      </div>
                    </div>
                  ))}
                  <div className='rounded-lg !base-border p-4 flex gap-1 items-center justify-center flex-col shadow-md'>
                    <i className={`bi bi-calculator text-lg`} />
                    <div>{t('common.table.total')}</div>
                    <div>
                      <NumericFormat
                        value={dailyPayments?.data?.reduce(
                          (ac, cu) => ac + cu?.sum,
                          0
                        )}
                        displayType={'text'}
                        allowNegative={false}
                        thousandSeparator={' '}
                        decimalScale={2}
                        className='bg-transparent font-bold'
                        suffix={' UZS'}
                      />
                    </div>
                  </div>
                </>
              ) : (
                ''
              )}
            </div>
          )}
        </div>
      </div>

      {/* <div className='grid grid-cols-12 col-span-12 gap-4 gap-y-2'>
        <div className='col-span-12 md:col-span-8 lg:col-span-5 dashboard-payments !base-border !shadow-md'>
          <div className='payments-title'>{t('boss.payment.title')}</div>
          <div className='w-[360px] h-[330px] flex items-center mx-auto'>
            {dailyPayments.isLoading || dailyPayments.isFetching ? (
              <div className='flex w-full items-center justify-center'>
                <CircularProgress size={30} color='inherit' />
              </div>
            ) : (
              dailyPayments.data &&
              dailyPayments.data.length > 0 && (
                <DoughnutChart
                  chartLabels={[...dailyPayments.data.map((item) => item.name)]}
                  chartDatasets={[
                    {
                      data: [
                        ...dailyPayments.data.map((item) => ({
                          total:
                            +item.sum +
                            (item?.valute
                              ? +item.valute * currencyData?.sum
                              : 0),
                          sum: item.sum,
                          valute: item?.valute ? item.valute : 0,
                        })),
                      ],
                      borderWidth: 4,
                      hoverBorderWidth: 0,
                      hoverOffset: 15,
                      borderRadius: 4,
                    },
                  ]}
                  optionPlugins={{
                    legend: {
                      display: true,
                      position: 'bottom',
                      align: 'left',
                      reverse: false,
                      labels: {
                        usePointStyle: true,
                        pointStyle: 'rectRounded',
                      },
                    },
                    tooltip: {
                      enabled: false,
                      external: doughnutTooltip('sum', 'valute', 'total'),
                    },
                  }}
                  options={{
                    layout: {
                      padding: {
                        top: 10,
                      },
                    },
                    parsing: {
                      key: 'total',
                    },
                  }}
                />
              )
            )}
          </div>
          <div className='payments-daily-status'>
            {t('boss.dashboard.statusToday')}:{' '}
            {dailyPayments.isLoading || dailyPayments.isFetching ? (
              <div className='flex ml-2'>
                <CircularProgress size={20} color='inherit' />
              </div>
            ) : (
              <NumericFormat
                value={dailyStatusSum}
                displayType={'text'}
                allowNegative={false}
                thousandSeparator={' '}
                decimalScale={3}
                className='payments-daily-status-sum bg-transparent'
                suffix=' UZS'
              />
            )}
          </div>
        </div>
        <div className='dashboard-homes col-span-12 lg:col-span-7'>
          <div className='grid grid-cols-12 gap-4'>
            <div className='col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-6'>
              <RoomQuantityCard
                status='free-homes'
                value={analyticData?.data?.freehomes}
                analyticData={analyticData}
                title={t('common.filter.emptyHomesCount')}
              />
            </div>
            <div className='col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-6'>
              <RoomQuantityCard
                status='sold-homes'
                value={analyticData?.data?.dailycontracts}
                analyticData={analyticData}
                title={t('boss.dashboard.soldToday')}
                onClick={() => setOpen(true)}
              />
            </div>
            <div className='col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-12'>
              <RoomQuantityCard
                status='ordered-homes'
                value={analyticData?.data?.orderhomes}
                analyticData={analyticData}
                title={t('boss.dashboard.orderedToday')}
              />
            </div>
          </div>
        </div>
      </div> */}
      <ContractsModal
        open={open}
        setOpen={setOpen}
        data={analyticData?.data?.contracts || []}
      />
      <DebtsModal
        open={openDebts}
        setOpen={setOpenDebts}
        data={analyticData?.data?.monthlyDebtsDetail || []}
      />
    </div>
  );
};
export default memo(Dashboard);

const getIcon = (name) => {
  switch (name) {
    case 'Naqd':
      return 'bi bi-cash text-blue-400';
    case 'P2P':
      return 'bi bi-arrow-left-right text-pink-400';
    case 'Bank':
      return 'bi bi-bank text-orange-500';
    case 'Karta':
      return 'bi bi-credit-card text-yellow-500';
    case 'Mijoz Balansi':
      return 'bi bi-currency-dollar text-indigo-600';

    default:
      return 'bi bi-three-dots text-green-500';
  }
};
const getHomeStatusIcon = (id) => {
  switch (id?.toString()) {
    case '1':
      return 'bi bi-house text-blue-500';
    case '2':
      return 'bi bi-house-heart text-orange-500';
    case '3':
      return 'bi bi-house-check text-green-500';
    case '4':
      return 'bi bi-house-slash text-gray-500';
    case '5':
      return 'bi bi-currency-dollar text-indigo-600';

    default:
      return 'bi bi-three-dots text-green-500';
  }
};
const getHomeStatusColor = (id) => {
  switch (id?.toString()) {
    case '1':
      return 'text-blue-500';
    case '2':
      return 'text-orange-500';
    case '3':
      return 'text-green-500';
    case '4':
      return 'text-gray-500';
    case '5':
      return 'text-indigo-600';

    default:
      return 'text-green-500';
  }
};
